/**
 * The other half of a slide's artwork: things the platform already holds.
 *
 * Two sources, one shape. REFERENCES are what this team saved as inspiration,
 * scoped like the References page: this brand's, or Everyone's (the untagged,
 * workspace-wide bucket). TRENDS are platform-wide, and
 * because a trend is a pattern rather than a clip, picking one is two steps:
 * choose the trend, then choose which of its example videos goes on the slide.
 *
 * A pick applies immediately — there is nothing to configure and nothing to
 * confirm, unlike generating an image. Only items we can actually serve are
 * listed: our own mirrors. An external CDN link dies within a day and a slide
 * that 403s in a client meeting is the failure this filter exists to prevent.
 */
import { useMemo, useState } from "react";
import { ChevronLeft, Image as ImageIcon, Play, Search } from "lucide-react";
import { Image } from "@/components/Image";
import { cn } from "@/utils/helpers";
import type { Reference } from "@/types/reference.type";
import type { Trend, TrendExample } from "@/types/trend.type";
import { useReferences } from "../../references/api/useReferences";
import { useTrendDetail, useTrends } from "../../trends/api/useTrends";
import { KitInput, KitSelect, KitSelectItem } from "../kit";

export type ExternalMediaPick =
  | { kind: "reference"; referenceId: string }
  | { kind: "trend"; trendExampleId: string };

export type ExternalMediaSource = "trends" | "references";

const PAGE_SIZE = 24;
/** Below this the server ignores `q` anyway, so searching would only look broken. */
const MIN_QUERY = 2;

export interface ExternalMediaPickerProps {
  onPick: (pick: ExternalMediaPick) => void;
  /** Locks the picker to one source when a host gives each source its own tab. */
  sourceOverride?: ExternalMediaSource;
  /** The brand the pick is for. Backs the references "This brand" scope. */
  brandId?: string;
}

type ReferenceScope = "brand" | "global";
const GLOBAL = "global";

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 items-center rounded-pill px-3.5 text-[13px] font-medium transition-colors",
        active
          ? "bg-co-accent-dim text-co-text"
          : "co-glass-control text-co-muted hover:text-co-text",
      )}
    >
      {children}
    </button>
  );
}

/**
 * A still frame, not a player. The `#t=0.1` fragment makes the browser seek and
 * paint that frame: with `preload="metadata"` alone several browsers fetch the
 * header and then show nothing until play. The fragment never reaches the
 * server, so it does not disturb the presigned query string.
 */
function stillSrc(url: string): string {
  return `${url}#t=0.1`;
}

/**
 * One item in either grid.
 *
 * The still comes from OUR copy wherever there is one. Platform cover URLs
 * (`posterUrl` here) are signed CDN links that die within about a day, exactly
 * like the video URLs we already refuse to serve, so most of them 403 by the
 * time anyone opens this. A `<video>` handles that on its own: a poster that
 * fails falls back to the first frame of the mirrored file, where an `<img>`
 * would just show a broken icon. `preload="metadata"` keeps that to a header
 * fetch rather than the whole clip.
 */
function ExternalTile({
  videoUrl,
  posterUrl,
  title,
  meta,
  previewMode = "portrait",
  onClick,
}: {
  videoUrl: string | null;
  posterUrl: string | null;
  title: string;
  meta: string | null;
  previewMode?: "portrait" | "contain";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col gap-2 text-left"
    >
      <div
        className={cn(
          "relative overflow-hidden rounded-card bg-[#0f0f0f] outline-offset-0 transition-colors group-hover:outline-2 group-hover:outline-co-accent",
          previewMode === "portrait" ? "aspect-[9/16]" : "aspect-[4/3]",
        )}
      >
        {videoUrl ? (
          <video
            src={stillSrc(videoUrl)}
            poster={posterUrl ?? undefined}
            muted
            playsInline
            preload="metadata"
            className={cn("size-full", previewMode === "portrait" ? "object-cover" : "object-contain")}
          />
        ) : posterUrl ? (
          <Image
            src={posterUrl}
            alt={title}
            className={cn("size-full", previewMode === "portrait" ? "object-cover" : "object-contain")}
          />
        ) : (
          <span className="grid size-full place-items-center text-white/35">
            <ImageIcon className="size-5" strokeWidth={1.4} />
          </span>
        )}
        {videoUrl ? (
          <span className="absolute inset-0 grid place-items-center bg-black/20 transition-colors group-hover:bg-black/35">
            <span className="grid size-8 place-items-center rounded-full bg-black/60 text-white">
              <Play className="size-3.5" fill="currentColor" strokeWidth={0} />
            </span>
          </span>
        ) : null}
      </div>
      <div className="min-w-0">
        <p className="truncate text-[12px] text-co-text">{title}</p>
        {meta ? <p className="truncate text-[11px] text-co-muted">{meta}</p> : null}
      </div>
    </button>
  );
}

function TileGrid({
  isLoading,
  isEmpty,
  empty,
  previewMode = "portrait",
  children,
}: {
  isLoading: boolean;
  isEmpty: boolean;
  empty: string;
  previewMode?: "portrait" | "contain";
  children: React.ReactNode;
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <div
            key={i}
            className={cn(
              "animate-pulse rounded-card border border-co-line bg-co-surface",
              previewMode === "portrait" ? "aspect-[9/16]" : "aspect-[4/3]",
            )}
          />
        ))}
      </div>
    );
  }

  if (isEmpty) {
    return <p className="py-8 text-center text-[13px] text-co-muted">{empty}</p>;
  }

  return <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">{children}</div>;
}

function compactCount(n: number | null): string | null {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
}

function exampleMeta(example: TrendExample): string | null {
  const parts = [
    example.authorHandle ? `@${example.authorHandle}` : null,
    compactCount(example.viewCount) ? `${compactCount(example.viewCount)} views` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/** Step two of the trend flow: which of this trend's videos goes on the slide. */
function TrendExamples({
  trendId,
  trendTitle,
  onBack,
  onPick,
}: {
  trendId: string;
  trendTitle: string;
  onBack: () => void;
  onPick: (pick: ExternalMediaPick) => void;
}) {
  const { data, isLoading } = useTrendDetail(trendId);
  // A broken mirror still has a thumbnail and its metrics, so it belongs on the
  // trends page — but there is no file to put on a slide, so not here.
  const examples = useMemo(
    () => (data?.examples ?? []).filter((e) => e.videoUrl && !e.videoBroken),
    [data],
  );

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 self-start text-[13px] text-co-muted transition-colors hover:text-co-text"
      >
        <ChevronLeft className="size-3.5" strokeWidth={2} />
        All trends
      </button>
      <p className="text-[13px] text-co-text">{trendTitle}</p>

      <TileGrid
        isLoading={isLoading}
        isEmpty={examples.length === 0}
        empty="No playable video saved for this trend."
      >
        {examples.map((example) => (
          <ExternalTile
            key={example.id}
            videoUrl={example.videoUrl}
            posterUrl={example.thumbnailUrl}
            title={example.caption?.trim() || example.platform}
            meta={exampleMeta(example)}
            onClick={() => onPick({ kind: "trend", trendExampleId: example.id })}
          />
        ))}
      </TileGrid>
    </div>
  );
}

function TrendsSource({
  query,
  onPick,
}: {
  query: string;
  onPick: (pick: ExternalMediaPick) => void;
}) {
  const [openTrend, setOpenTrend] = useState<Trend | null>(null);

  const { trends, isLoading } = useTrends({
    q: query.length >= MIN_QUERY ? query : undefined,
    pageSize: PAGE_SIZE,
    sort: "newest",
  });

  // A trend with no playable preview has no playable examples either, so it
  // would only lead to an empty second step.
  const listed = useMemo(
    () => trends.filter((t) => t.previewVideo?.videoUrl && !t.previewVideo.videoBroken),
    [trends],
  );

  if (openTrend) {
    return (
      <TrendExamples
        trendId={openTrend.id}
        trendTitle={openTrend.title}
        onBack={() => setOpenTrend(null)}
        onPick={onPick}
      />
    );
  }

  return (
    <TileGrid
      isLoading={isLoading}
      isEmpty={listed.length === 0}
      empty={query ? "No trends match that." : "No trends with playable video yet."}
    >
      {listed.map((trend) => (
        <ExternalTile
          key={trend.id}
          videoUrl={trend.previewVideo?.videoUrl ?? null}
          posterUrl={trend.previewVideo?.thumbnailUrl ?? null}
          title={trend.title}
          meta={`${trend.stats.exampleCount} video${trend.stats.exampleCount === 1 ? "" : "s"}`}
          onClick={() => setOpenTrend(trend)}
        />
      ))}
    </TileGrid>
  );
}

/** A saved clip, or a saved trend (whose media is its lead clip). */
function isVideoReference(reference: Reference): boolean {
  return reference.type === "video" || reference.type === "trend";
}

function referenceMeta(reference: Reference): string | null {
  const parts = [
    reference.type,
    reference.platform,
    reference.authorHandle ? `@${reference.authorHandle}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

function ReferencesSource({
  query,
  scopeBrandId,
  onPick,
}: {
  query: string;
  /** A brand id (that brand's references) or "global" (Everyone's). */
  scopeBrandId: string;
  onPick: (pick: ExternalMediaPick) => void;
}) {
  // Fetched unfiltered by type and narrowed here: the API's `type` param takes
  // ONE value, and a slide accepts videos (a saved trend plays its lead clip)
  // and images. Articles and notes are dropped alongside anything we never
  // mirrored.
  const { references, isLoading } = useReferences({
    brandId: scopeBrandId,
    q: query.length >= MIN_QUERY ? query : undefined,
    limit: 60,
  });

  const listed = useMemo(
    () =>
      references.filter(
        (r) =>
          (isVideoReference(r) && r.videoUrl) || (r.type === "image" && r.imageUrl),
      ),
    [references],
  );

  return (
    <TileGrid
      isLoading={isLoading}
      isEmpty={listed.length === 0}
      empty={
        query
          ? "No references match that."
          : `No video, trend or image references saved for ${scopeBrandId === GLOBAL ? "Everyone" : "this brand"} yet.`
      }
      previewMode="contain"
    >
      {listed.map((reference) => (
        <ExternalTile
          key={reference.id}
          videoUrl={isVideoReference(reference) ? reference.videoUrl : null}
          posterUrl={reference.imageUrl ?? reference.thumbnailUrl}
          title={reference.title}
          meta={referenceMeta(reference)}
          previewMode="contain"
          onClick={() => onPick({ kind: "reference", referenceId: reference.id })}
        />
      ))}
    </TileGrid>
  );
}

export function ExternalMediaPicker({ onPick, sourceOverride, brandId }: ExternalMediaPickerProps) {
  const [internalSource, setInternalSource] = useState<ExternalMediaSource>("trends");
  const source = sourceOverride ?? internalSource;
  // Same two scopes as the References page. Without a brand there is no
  // "This brand" to show, so it starts on Everyone and the option is disabled.
  const [scope, setScope] = useState<ReferenceScope>(brandId ? "brand" : "global");
  const effectiveScope: ReferenceScope = brandId ? scope : "global";
  const [search, setSearch] = useState("");
  // Submitted rather than typed, like the trends and references pages: both
  // searches are semantic server-side, so a query per keystroke is a query per
  // keystroke's worth of embedding.
  const [query, setQuery] = useState("");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        {!sourceOverride ? (
          <div className="flex gap-2">
            <Chip active={source === "trends"} onClick={() => setInternalSource("trends")}>
              Trends
            </Chip>
            <Chip active={source === "references"} onClick={() => setInternalSource("references")}>
              References
            </Chip>
          </div>
        ) : null}
        <form
          className="min-w-[200px] flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(search.trim());
          }}
        >
          <KitInput
            icon={Search}
            value={search}
            placeholder={source === "trends" ? "Search trends" : "Search references"}
            onChange={(e) => setSearch(e.target.value)}
          />
        </form>
        {source === "references" ? (
          <KitSelect
            aria-label="Whose references"
            selectedKey={effectiveScope}
            onSelectionChange={(key) => {
              if (key != null) setScope(key as ReferenceScope);
            }}
            disabledKeys={brandId ? [] : ["brand"]}
            buttonClassName="w-auto min-w-[150px]"
          >
            <KitSelectItem id="brand" textValue="This brand">
              This brand
            </KitSelectItem>
            <KitSelectItem id="global" textValue="Everyone">
              Everyone
            </KitSelectItem>
          </KitSelect>
        ) : null}
      </div>

      {source === "trends" ? (
        <TrendsSource query={query} onPick={onPick} />
      ) : (
        <ReferencesSource
          query={query}
          scopeBrandId={effectiveScope === "brand" && brandId ? brandId : GLOBAL}
          onPick={onPick}
        />
      )}
    </div>
  );
}

export default ExternalMediaPicker;
